import type {
  ItemCreate,
  ItemDetail,
  ItemListQuery,
  ItemListResponse,
  ItemUpdate,
} from '@messe/shared'
import { apiRequest } from './api.js'

export function listItems(query: Partial<ItemListQuery> = {}): Promise<ItemListResponse> {
  const params = new URLSearchParams()

  if (query.search) params.set('search', query.search)
  if (query.eventId) params.set('eventId', query.eventId)
  if (query.supplierItemId) params.set('supplierItemId', query.supplierItemId)
  if (query.createdByUserId) params.set('createdByUserId', query.createdByUserId)
  if (query.capturedFrom) params.set('capturedFrom', query.capturedFrom)
  if (query.capturedTo) params.set('capturedTo', query.capturedTo)
  if (query.needsReviewOnly) params.set('needsReviewOnly', 'true')
  if (query.includeDeleted) params.set('includeDeleted', 'true')
  if (query.sort) params.set('sort', query.sort)
  if (query.direction) params.set('direction', query.direction)
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.offset !== undefined) params.set('offset', String(query.offset))

  const suffix = params.size > 0 ? `?${params.toString()}` : ''
  return apiRequest<ItemListResponse>(`/items${suffix}`)
}

export function getItem(id: string): Promise<ItemDetail> {
  return apiRequest<ItemDetail>(`/items/${id}`)
}

export function createItem(input: ItemCreate): Promise<ItemDetail> {
  return apiRequest<ItemDetail>('/items', { method: 'POST', body: input })
}

export function updateItem(id: string, changes: ItemUpdate): Promise<ItemDetail> {
  return apiRequest<ItemDetail>(`/items/${id}`, { method: 'PATCH', body: changes })
}

export async function deleteItem(id: string): Promise<void> {
  // 204, so there is nothing to return.
  await apiRequest<undefined>(`/items/${id}`, { method: 'DELETE' })
}

export function restoreItem(id: string): Promise<ItemDetail> {
  return apiRequest<ItemDetail>(`/items/${id}/restore`, { method: 'POST' })
}

/**
 * The filter as the page holds it, before it becomes a query string.
 *
 * Empty strings rather than undefined, because these are bound straight to
 * form controls and a controlled input with an undefined value switches to
 * uncontrolled — React then warns, and the field stops updating.
 */
export interface ItemFilterState {
  search: string
  eventId: string
  supplierItemId: string
  createdByUserId: string
  capturedFrom: string
  capturedTo: string
  needsReviewOnly: boolean
  includeDeleted: boolean
}

export function emptyFilter(): ItemFilterState {
  return {
    search: '',
    eventId: '',
    supplierItemId: '',
    createdByUserId: '',
    capturedFrom: '',
    capturedTo: '',
    needsReviewOnly: false,
    includeDeleted: false,
  }
}

/** How many filters are set, for the badge on the filter panel. */
export function activeFilterCount(filter: ItemFilterState): number {
  let count = 0
  if (filter.search.trim() !== '') count += 1
  if (filter.eventId !== '') count += 1
  if (filter.supplierItemId !== '') count += 1
  if (filter.createdByUserId !== '') count += 1
  if (filter.capturedFrom !== '') count += 1
  if (filter.capturedTo !== '') count += 1
  if (filter.needsReviewOnly) count += 1
  if (filter.includeDeleted) count += 1
  return count
}

/**
 * Turns the form state into a query.
 *
 * The two date fields come from `<input type="date">`, which yields a bare
 * YYYY-MM-DD. The API wants an instant, and the boundaries have to bracket the
 * whole day — otherwise "captured up to the 5th" silently excludes everything
 * captured on the 5th.
 */
export function toQuery(
  filter: ItemFilterState,
  limit: number,
  offset: number,
): Partial<ItemListQuery> {
  return {
    ...(filter.search.trim() !== '' ? { search: filter.search.trim() } : {}),
    ...(filter.eventId !== '' ? { eventId: filter.eventId } : {}),
    ...(filter.supplierItemId !== '' ? { supplierItemId: filter.supplierItemId } : {}),
    ...(filter.createdByUserId !== '' ? { createdByUserId: filter.createdByUserId } : {}),
    ...(filter.capturedFrom !== ''
      ? { capturedFrom: new Date(`${filter.capturedFrom}T00:00:00`).toISOString() }
      : {}),
    ...(filter.capturedTo !== ''
      ? { capturedTo: new Date(`${filter.capturedTo}T23:59:59.999`).toISOString() }
      : {}),
    needsReviewOnly: filter.needsReviewOnly,
    includeDeleted: filter.includeDeleted,
    limit,
    offset,
  }
}
